import React from 'react';
import { stubTrue } from 'lodash';
import {
    creativeTitleValidations,
    maxCreativeTitleLength,
    TITLE_WARNING_THRESHOLD,
    creativeDescriptionValidations,
    urlValidations,
} from 'modules/campaigns/config';
import { deleteCreative } from 'modules/campaigns/flows';
import { setSelectedRows } from 'modules/campaigns/modules/campaigns-reports/actions';
import {
    FormattedMessageWithTooltipHeaderRenderer,
    FormattedMessageHeaderRenderer,
    ImageCellRenderer,
    IMAGE_CELL_RENDERER_MODES,
    EllipsisCellRenderer,
    StateCellRenderer,
    withCreativeEditIndication,
    CreativeStatusRenderer,
    CreativeActionsCellRenderer,
    ContentCellRenderer,
    formattedNumberValueGetter,
    gridNumberFormatter,
    CheckboxHeaderRenderer,
    CheckboxCellRenderer,
    CreativeIdCellRenderer,
    RssBulkDisabledTooltip,
    StoppedCreativeBulkDisabledTooltip,
} from 'modules/campaigns/modules/campaigns-reports/components';
import {
    editableColumn,
    staticColumn,
    keyColumn,
    leftPinnedColumn,
    toggleColumn,
    numericColumn,
    rightPinnedColumn,
    actionsColumnType,
    checkBoxSelectionColumn,
    lockedColumn,
    bypassMetadataFilterType,
} from 'modules/campaigns/modules/campaigns-reports/config/columns';
import { nonDefaultHeaderComponentParams } from 'modules/campaigns/modules/campaigns-reports/config/fields';
import * as FIELDS from 'modules/campaigns/modules/campaigns-reports/config/fields';
import {
    isCreativeRejected,
    isCreativeStopped,
    isRssChild,
} from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { CREATIVE_REPORT_ID } from 'modules/campaigns/modules/campaigns-reports/config/reports/creative/creativeReportId';
import {
    editCreative,
    saveCreative,
    previewCreative,
    duplicateCreative,
    scheduleCreative,
} from 'modules/campaigns/modules/campaigns-reports/flows';
import { normalizeColumnDefs } from 'modules/campaigns/modules/campaigns-reports/utils';
import {
    MAX_DESCRIPTION_LENGTH,
    DESCRIPTION_WARNING_THRESHOLD,
} from 'modules/campaigns/modules/creative-creator/components/ContentField/config';
import { PERFORMANCE_RECOMMENDATIONS } from 'modules/campaigns/modules/performance-recommendations/config';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { CREATIVE_REALTIME_REPORT_ID } from './creativeRealtimeReportId';

const isDisabled = data => {
    return isRssChild(data) || isCreativeStopped(data);
};

const tooltipContent = data =>
    (isRssChild(data) && <RssBulkDisabledTooltip />) ||
    (isCreativeStopped(data) && <StoppedCreativeBulkDisabledTooltip />) ||
    null;

const creativeReportColumnDefinition = [
    {
        headerComponent: CheckboxHeaderRenderer,
        headerName: FIELDS.IS_SELECTED.label,
        field: FIELDS.IS_SELECTED.field,
        type: [leftPinnedColumn, checkBoxSelectionColumn, staticColumn],
        cellRendererParams: {
            onClick: setSelectedRows,
            isDisabled,
            tooltipContent,
        },
        headerComponentParams: {
            onClick: setSelectedRows,
            isDisabled,
            tooltipContent,
        },
        valueGetter: FIELDS.IS_SELECTED.valueGetter,
        cellRenderer: CheckboxCellRenderer,
        csv: false,
        permissions: {
            permissions: ['CAMPAIGNS_ITEMS_BULK_UPDATE'],
        },
        accountConfigurations: {
            [FEATURE_FLAGS.CREATIVES_BULK_ACTIONS_ENABLED]: 'true',
        },
    },
    {
        headerName: FIELDS.IS_ACTIVE.label,
        field: FIELDS.IS_ACTIVE.field,
        type: [lockedColumn, toggleColumn, leftPinnedColumn, staticColumn],
        cellRendererParams: {
            ...FIELDS.IS_ACTIVE.renderParams,
            isDisabled: data => isCreativeStopped(data) || isCreativeRejected(data),
            reportId: CREATIVE_REPORT_ID,
        },
        valueChangeHandler: saveCreative,
        cellRenderer: StateCellRenderer,
        csv: false,
    },
    {
        headerName: FIELDS.CUSTOM_CREATIVE_NAME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CUSTOM_CREATIVE_NAME.headerComponentParams,
        field: FIELDS.CUSTOM_CREATIVE_NAME.field,
        type: [leftPinnedColumn, editableColumn],
        editable: stubTrue,
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2 },
        cellEditor: FIELDS.CUSTOM_CREATIVE_NAME.cellEditor,
        valueChangeHandler: saveCreative,
        valueFormatter: FIELDS.CUSTOM_CREATIVE_NAME.valueFormatter,
        accountConfigurations: { [FEATURE_FLAGS.SHOW_CUSTOM_DATA_IN_CREATIVE_FORM]: 'true' },
    },
    {
        headerName: FIELDS.CUSTOM_ID.label,
        field: FIELDS.CUSTOM_ID.field,
        type: [leftPinnedColumn, editableColumn],
        editable: stubTrue,
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2 },
        cellEditor: FIELDS.CUSTOM_ID.cellEditor,
        valueChangeHandler: saveCreative,
        valueFormatter: FIELDS.CUSTOM_CREATIVE_NAME.valueFormatter,
        accountConfigurations: { [FEATURE_FLAGS.SHOW_CUSTOM_DATA_IN_CREATIVE_FORM]: 'true' },
    },
    {
        headerName: FIELDS.ID.label,
        field: FIELDS.ID.field,
        type: [numericColumn],
        cellRenderer: CreativeIdCellRenderer,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: nonDefaultHeaderComponentParams(CREATIVE_REALTIME_REPORT_ID),
        cellRendererParams: { isSearchable: true },
    },
    {
        headerName: FIELDS.THUMBNAIL_URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.TITLE.headerComponentParams,
        field: FIELDS.THUMBNAIL_URL.field,
        type: [leftPinnedColumn, editableColumn],
        cellRenderer: ImageCellRenderer,
        cellRendererParams: {
            mode: IMAGE_CELL_RENDERER_MODES.DEFAULT,
        },
        singleClickEdit: false,
        onCellClicked: editCreative,
        width: 120,
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
        sortable: false,
    },
    {
        headerName: FIELDS.THUMBNAIL_URL_COMFORTABLE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.THUMBNAIL_URL_COMFORTABLE.headerComponentParams,
        field: FIELDS.THUMBNAIL_URL_COMFORTABLE.field,
        type: [leftPinnedColumn, editableColumn, bypassMetadataFilterType],
        valueGetter: ({ data }) => {
            const { thumbnailUrl } = data;
            return thumbnailUrl;
        },
        cellRenderer: ImageCellRenderer,
        cellRendererParams: {
            mode: IMAGE_CELL_RENDERER_MODES.COMFORTABLE,
        },
        onCellClicked: editCreative,
        singleClickEdit: false,
        width: 186,
        csv: [
            {
                headerName: 'Media',
                field: 'thumbnailUrl',
            },
        ],
        visibilityCondition: ({ isComfortableViewEnabled }) => isComfortableViewEnabled,
        sortable: false,
    },
    {
        headerName: FIELDS.CONTENT.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CONTENT.headerComponentParams,
        type: [editableColumn, bypassMetadataFilterType, leftPinnedColumn],
        valueGetter: ({ data }) => {
            const { title, description, cta } = data;
            return { title, description, cta };
        },
        field: FIELDS.CONTENT.field,
        cellRenderer: ContentCellRenderer,
        cellRendererParams: {
            lines: 2,
            ctaMsgIdPrefix: 'creative.creator.content.cta.option',
            isSearchable: true,
        },
        singleClickEdit: false,
        width: 200,
        csv: [
            {
                headerName: 'Title',
                field: 'title',
            },
            {
                headerName: 'Description',
                field: 'description',
            },
            ...FIELDS.CTA.csv,
        ],
        visibilityCondition: ({ isComfortableViewEnabled }) => isComfortableViewEnabled,
        sortable: false,
        onCellClicked: editCreative,
    },
    {
        headerName: FIELDS.TITLE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.TITLE.headerComponentParams,
        field: FIELDS.TITLE.field,
        type: [keyColumn, editableColumn, leftPinnedColumn],
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2, isSearchable: true },
        cellEditor: FIELDS.TITLE.cellEditor,
        cellEditorParams: {
            validations: creativeTitleValidations,
            maxLength: maxCreativeTitleLength,
            warningThreshold: TITLE_WARNING_THRESHOLD,
        },
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
        width: 120,
        sortable: false,
    },
    {
        headerName: FIELDS.URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.URL.headerComponentParams,
        field: FIELDS.URL.field,
        type: [editableColumn],
        cellRenderer: FIELDS.URL.cellRenderer,
        width: 150,
        cellEditor: FIELDS.URL.cellEditor,
        cellEditorParams: {
            validations: urlValidations,
        },
        valueChangeHandler: saveCreative,
        cellRendererParams: { isSearchable: true },
    },
    {
        headerName: FIELDS.DESCRIPTION.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.DESCRIPTION.headerComponentParams,
        field: FIELDS.DESCRIPTION.field,
        type: [editableColumn],
        cellRenderer: EllipsisCellRenderer,
        width: 250,
        cellEditor: FIELDS.DESCRIPTION.cellEditor,
        cellEditorParams: {
            validations: creativeDescriptionValidations,
            maxLength: MAX_DESCRIPTION_LENGTH,
            warningThreshold: DESCRIPTION_WARNING_THRESHOLD,
        },
        valueChangeHandler: saveCreative,
        cellRendererParams: { isSearchable: true },
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
    },
    {
        headerName: FIELDS.STATUS.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.STATUS.headerComponentParams,
        field: FIELDS.STATUS.field,
        type: [leftPinnedColumn],
        cellRenderer: CreativeStatusRenderer,
        flex: 1,
        minWidth: 200,
        cellRendererParams: {
            ...FIELDS.STATUS.renderParams,
            recommendationTypes: [
                PERFORMANCE_RECOMMENDATIONS.LOW_PERFORMING_ITEM,
                PERFORMANCE_RECOMMENDATIONS.LOW_RESOLUTION_IC,
                PERFORMANCE_RECOMMENDATIONS.ITEM_WITHOUT_DESCRIPTION,
                PERFORMANCE_RECOMMENDATIONS.ITEM_WITHOUT_CTA,
            ],
        },
        csvValueGetter: FIELDS.STATUS.csvValueGetter,
    },
    {
        headerName: FIELDS.CTA.label,
        field: FIELDS.CTA.field,
        valueGetter: FIELDS.CTA.valueGetter,
        type: [editableColumn],
        cellRenderer: FIELDS.CTA.cellRenderer,
        cellRendererParams: FIELDS.CTA.renderParams,
        cellEditor: FIELDS.CTA.cellEditor,
        valueChangeHandler: saveCreative,
        csv: FIELDS.CTA.csv,
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
    },
    {
        headerName: FIELDS.START_DATE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.START_DATE.headerComponentParams,
        field: FIELDS.START_DATE.field,
        valueFormatter: FIELDS.START_DATE.valueFormatter,
        csvValueGetter: FIELDS.START_DATE.csvValueGetter,
    },
    {
        headerName: FIELDS.END_DATE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.END_DATE.headerComponentParams,
        field: FIELDS.END_DATE.field,
        valueFormatter: FIELDS.END_DATE.valueFormatter,
        csvValueGetter: FIELDS.END_DATE.csvValueGetter,
    },
    {
        headerName: FIELDS.ACTIONS_COLUMN.label,
        field: FIELDS.ACTIONS_COLUMN.field,
        type: [rightPinnedColumn, actionsColumnType, staticColumn],
        minWidth: 150,
        maxWidth: 150,
        cellRendererParams: {
            onScheduler: scheduleCreative,
            onDelete: deleteCreative,
            onPreview: previewCreative,
            onEdit: editCreative,
            onDuplicate: duplicateCreative,
        },
        cellRenderer: CreativeActionsCellRenderer,
        csv: false,
    },
    ...[
        FIELDS.VISIBLE_IMPRESSIONS,
        FIELDS.VCTR,
        FIELDS.CLICKS,
        FIELDS.AVERAGE_CPC,
        FIELDS.CVR,
        FIELDS.CVR_CLICKS,
        FIELDS.CVR_VIEWS,
        FIELDS.CONVERSIONS,
        FIELDS.CONVERSIONS_CLICKS,
        FIELDS.CONVERSIONS_VIEWS,
        FIELDS.CPA,
        FIELDS.CPA_CLICKS,
        FIELDS.CPA_VIEWS,
        FIELDS.VCPM,
        FIELDS.IMPRESSIONS,
        FIELDS.SPENT,
        FIELDS.CONVERSIONS_VALUE,
        FIELDS.ROAS,
    ].map(fieldConfig => ({
        headerName: fieldConfig.label,
        headerValueGetter: fieldConfig.headerValueGetter || FormattedMessageWithTooltipHeaderRenderer,
        headerComponentParams: fieldConfig.headerComponentParams,
        field: fieldConfig.field,
        type: [numericColumn],
        valueFormatter: gridNumberFormatter,
        cellRendererParams: fieldConfig.renderParams,
        csvValueGetter: fieldConfig.csvValueGetter || formattedNumberValueGetter,
        referenceColId: fieldConfig.referenceColId,
        permissions: fieldConfig.permissions,
        visibilityCondition: fieldConfig.visibilityCondition,
    })),
];

export default normalizeColumnDefs(
    creativeReportColumnDefinition,
    CREATIVE_REALTIME_REPORT_ID,
    withCreativeEditIndication
);
